// Typed client for the backend /api contract: the ONLY coupling between the SPA
// and the Go backend (9). Same-origin in production (the binary serves this
// SPA) and via the Vite dev proxy in development, so the session cookie flows
// automatically. Mutations carry the CSRF token (double-submit; the backend
// echoes authkit's CSRF cookie value in the `X-CSRF-Token` header; see authkit).

import { ERROR_MESSAGES } from "./errorCatalog.generated";

const BASE = "/api";

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public body?: unknown,
    // Stable machine code from the RFC 9457 envelope; the switch key for UI logic
    // (e.g. step-up) and the lookup key into the shared ERROR_MESSAGES catalog.
    public code?: string,
    // Seconds until the caller may retry, from the `Retry-After` header on a 429
    // (login throttling sends the exact remaining lockout). Undefined otherwise.
    public retryAfter?: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
  get isUnauthorized() {
    return this.status === 401;
  }
  get isForbidden() {
    return this.status === 403;
  }
  get isRateLimited() {
    return this.status === 429;
  }
}

// errorFromResponse builds the ApiError for a non-2xx response: the ONE place the
// problem+json envelope is decoded, so every caller (api.* and the auth form
// helpers, which post form-encoded bodies outside `request`) renders the same
// text for the same failure instead of leaking the raw envelope into the UI.
export async function errorFromResponse(res: Response): Promise<ApiError> {
  let body: unknown;
  let code: string | undefined;
  let message = res.statusText;
  try {
    body = await res.json();
    // Canonical error envelope is RFC 9457 problem+json with a stable `code`.
    // The shared ERROR_MESSAGES catalog (generated from the backend registry)
    // is the source of truth for display text, so the client renders from the
    // CODE, not the server's prose. Fall back to the server `detail` (then the
    // legacy `error`/`title`) only for a code the client doesn't know yet.
    if (body && typeof body === "object") {
      const b = body as {
        code?: unknown;
        detail?: unknown;
        error?: unknown;
        error_description?: unknown;
        title?: unknown;
      };
      // authkit v2's OAuth2-style envelope carries the machine code in `error`
      // and the prose in `error_description`; problem+json carries `code`.
      if (typeof b.code === "string") code = b.code;
      else if (typeof b.error === "string") code = b.error;
      const mapped = code ? ERROR_MESSAGES[code] : undefined;
      const msg = mapped ?? b.detail ?? b.error_description ?? b.title ?? b.error;
      if (msg != null) message = String(msg);
    }
  } catch {
    // Not JSON (a proxy's plain-text 502, say): fall back to the raw body.
    try {
      message = (await res.text()) || message;
    } catch {
      /* ignore */
    }
  }
  return new ApiError(res.status, message, body, code, retryAfterSeconds(res));
}

// retryAfterSeconds reads the `Retry-After` header (RFC 9110: delta-seconds or an
// HTTP-date). Returns undefined when absent or unparseable, so callers can tell
// "no lockout information" from "retry now".
//
// Same-origin today, so the header is readable without CORS ceremony; a split
// frontend (9) would need `Access-Control-Expose-Headers: Retry-After` on the API.
function retryAfterSeconds(res: Response): number | undefined {
  const raw = res.headers.get("Retry-After");
  if (!raw) return undefined;
  const secs = Number(raw);
  if (Number.isFinite(secs)) return Math.max(0, Math.ceil(secs));
  const date = Date.parse(raw);
  if (Number.isNaN(date)) return undefined;
  return Math.max(0, Math.ceil((date - Date.now()) / 1000));
}

let csrfToken: string | null = null;
// Single-flight guard: the in-flight fetch PROMISE, not just the resolved token.
// Two concurrent first mutations would otherwise both see csrfToken===null and
// fire two GET /auth/csrf requests; if the backend rotates the cookie per issue,
// the first token no longer matches the second (cookie) and the request 403s.
// Concurrent callers share this one promise instead.
let csrfInflight: Promise<string> | null = null;

// ensureCsrf fetches (and caches) a CSRF token. GET /api/auth/csrf returns
// {csrfToken} and sets the matching cookie; we echo the value in X-CSRF-Token.
async function ensureCsrf(): Promise<string> {
  if (csrfToken) return csrfToken;
  if (csrfInflight) return csrfInflight;
  csrfInflight = (async () => {
    const res = await fetch(`${BASE}/auth/csrf`, { credentials: "same-origin" });
    if (!res.ok) throw new ApiError(res.status, "could not obtain CSRF token");
    const data = (await res.json()) as { csrfToken: string };
    csrfToken = data.csrfToken;
    return csrfToken;
  })();
  try {
    return await csrfInflight;
  } finally {
    // Clear the in-flight promise so a later call re-fetches on failure (and so
    // an invalidated token can be re-obtained). The resolved value lives in
    // csrfToken; this only holds the pending fetch.
    csrfInflight = null;
  }
}

// On a 403 the token may be stale (rotated/expired), so drop it so the next
// mutation re-fetches.
function invalidateCsrf() {
  csrfToken = null;
}

const UNSAFE = new Set(["POST", "PUT", "PATCH", "DELETE"]);

// Step-up re-auth (6.5): a sensitive money action (e.g. void) answers 403 with
// {code:"step_up_required"} when the long-lived POS session needs a fresh PIN.
// The app registers a handler that prompts for the PIN; on success we retry the
// original request once. Kept here so every api.* call is transparently covered.
type StepUpHandler = () => Promise<boolean>;
let stepUpHandler: StepUpHandler | null = null;
export function setStepUpHandler(fn: StepUpHandler | null) {
  stepUpHandler = fn;
}

interface RequestOptions {
  method?: string;
  /** JSON body (sets Content-Type: application/json). */
  json?: unknown;
  /** Pre-encoded form body for the auth endpoints (application/x-www-form-urlencoded). */
  form?: Record<string, string>;
  signal?: AbortSignal;
  /** Don't throw on non-2xx; return the Response for the caller to inspect. */
  raw?: boolean;
  /** Client-supplied idempotency key for replay-safe creates (offline tolerance, 1). */
  idempotencyKey?: string;
}

// Paginated<T> is the uniform envelope every list endpoint returns ( backend
// internal/platform/page): a window of items plus whether another page follows.
// `has_more` (the page came back full) is enough to drive Prev/Next. `total` is
// OPTIONAL: endpoints that can afford the COUNT(*) send it so the UI can show
// "of N" and numbered pages; endpoints on large/hot tables omit it and the
// pager falls back to Prev/Next.
export interface Paginated<T> {
  items: T[];
  limit: number;
  offset: number;
  has_more: boolean;
  total?: number;
}

// PageParams is the matching request window. `q` is the free-text search.
export interface PageParams {
  q?: string;
  limit?: number;
  offset?: number;
}

// qs builds a query string from defined, non-empty params, returning "" or
// "?a=1&b=2", for list endpoints' filters.
export function qs(
  params: Record<string, string | number | boolean | undefined>,
): string {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== "" && v !== false) u.set(k, String(v));
  }
  const s = u.toString();
  return s ? `?${s}` : "";
}

// newIdempotencyKey returns a fresh client key for a create mutation.
export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

async function request(
  path: string,
  opts: RequestOptions = {},
  retried = false,
): Promise<Response> {
  const method = (opts.method ?? "GET").toUpperCase();
  const headers: Record<string, string> = {};
  let body: BodyInit | undefined;

  if (opts.json !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(opts.json);
  } else if (opts.form) {
    headers["Content-Type"] = "application/x-www-form-urlencoded";
    body = new URLSearchParams(opts.form).toString();
  }

  if (opts.idempotencyKey) {
    headers["Idempotency-Key"] = opts.idempotencyKey;
  }

  if (UNSAFE.has(method)) {
    headers["X-CSRF-Token"] = await ensureCsrf();
  }

  const res = await fetch(`${BASE}${path}`, {
    method,
    headers,
    body,
    credentials: "same-origin",
    signal: opts.signal,
  });

  if (res.status === 403) {
    invalidateCsrf();
    // A step-up challenge: prompt for the PIN, then retry the original request
    // once. Peek a clone so the caller can still read the body if we don't retry.
    if (!retried && stepUpHandler) {
      let code: string | undefined;
      try {
        code = ((await res.clone().json()) as { code?: string }).code;
      } catch {
        /* not JSON; fall through */
      }
      if (code === "step_up_required" && (await stepUpHandler())) {
        return request(path, opts, true);
      }
    }
  }
  return res;
}

async function parse<T>(res: Response): Promise<T> {
  if (!res.ok) throw await errorFromResponse(res);
  if (res.status === 204) return undefined as T;
  const ct = res.headers.get("Content-Type") ?? "";
  return ct.includes("application/json")
    ? ((await res.json()) as T)
    : (undefined as T);
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) =>
    request(path, { signal }).then(parse<T>),
  post: <T>(path: string, json?: unknown, idempotencyKey?: string) =>
    request(path, { method: "POST", json, idempotencyKey }).then(parse<T>),
  put: <T>(path: string, json?: unknown) =>
    request(path, { method: "PUT", json }).then(parse<T>),
  patch: <T>(path: string, json?: unknown) =>
    request(path, { method: "PATCH", json }).then(parse<T>),
  del: <T>(path: string, json?: unknown) => request(path, { method: "DELETE", json }).then(parse<T>),

  /** Low-level form POST for the auth endpoints (which read form values). */
  postForm: (path: string, form: Record<string, string>) =>
    request(path, { method: "POST", form, raw: true }),

  ensureCsrf,
  invalidateCsrf,
};
